import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TopAdminNav } from "../layouts/TopAdminNav";
import BackButton from "../components/backButton";
import { First } from "../components/registerStudent/First";
import { Second } from "../components/registerStudent/Second";
import Third from "../components/registerStudent/Third";
import { defaultUrl } from "../features/actions";
import {
  uploadImageToCloudinary,
  validateUploadFile,
} from "../utilities/importantFns";
import { makeFetchRequest, makePostRequest } from "../utilities/common";
import { UserCheck } from "lucide-react";
import toast from "react-hot-toast";

// Using the same PRIMARY color for consistency
const PRIMARY = "#0F2A6B";

export const RegisterStudent = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    surname: "",
    firstname: "",
    othername: "",
    gender: "",
    date_of_birth: "",
    email: "",
    phone_number: "",
    classroom_id: "",
    house_address: "",
    city: "",
    state: "",
    country: "",
    admission_date: "",
    zip_code: "",
    profile_picture: "",
    parent_id: [""],
    contact_name: [""],
    phone: [""],
    relationship: [""],
    condition: ["none"],
    extent: [],
  });

  const handleChange = ({ name, value, files }: any) => {
    if (files?.length > 0) {
      const validation = validateUploadFile(files[0], "image");
      if (!validation.valid) {
        toast.error(validation.message);
        return;
      }
    }
    setForm({ ...form, [name]: files?.length > 0 ? files[0] : value });
  };

  const nextStep = (page: number) => {
    setStep(page ? page : step + 1);
  };

  const handleSubmit = async (parentIDs: string[]) => {
    // Make shallow copy of the form
    const formData = form;
    setLoading(true);

    // if media is available
    let profile_pictureString;
    if (formData.profile_picture) {
      profile_pictureString = await uploadImageToCloudinary(
        formData.profile_picture,
      );
      formData.profile_picture = profile_pictureString || "";
    }

    const body = {
      surname: form.surname.trim(),
      firstname: form.firstname.trim(),
      othername: form.othername.trim(),
      gender: form.gender.trim(),
      date_of_birth: form.date_of_birth.trim(),
      email: form.email.trim(),
      phone_number: form.phone_number.trim(),
      classroom_id: form.classroom_id.trim(),
      house_address: form.house_address.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      country: form.country.trim(),
      admission_date: form.admission_date.trim(),
      zip_code: form.zip_code.trim(),
      profile_picture: formData.profile_picture.trim(),
      parent_id: parentIDs,
      contact_name: [form.contact_name],
      phone: [form.phone],
      relationship: [form.relationship],
      condition: [form.condition],
      extent: [form.extent],
    };

    await makePostRequest(`${defaultUrl}/students/create`, body);
    setLoading(false);

    setForm({
      surname: "",
      firstname: "",
      othername: "",
      gender: "",
      date_of_birth: "",
      email: "",
      phone_number: "",
      classroom_id: "",
      house_address: "",
      city: "",
      state: "",
      country: "",
      admission_date: "",
      zip_code: "",
      profile_picture: "",
      parent_id: [""],
      contact_name: [""],
      phone: [""],
      relationship: [""],
      condition: [],
      extent: [],
    });

    navigate(-1);
    navigate("/admin/student/student_data");
  };

  const renderContent = () => {
    switch (step) {
      case 1:
        return (
          <First nextStep={nextStep} handleChange={handleChange} form={form} />
        );
      case 2:
        return (
          <Third
            nextStep={nextStep}
            handleChange={handleChange}
            form={form}
            loading={loading}
          />
        );
      case 3:
        return (
          <Second
            nextStep={nextStep}
            handleSubmit={handleSubmit}
            handleChange={handleChange}
            form={form}
            loading={loading}
            setLoading={setLoading}
          />
        );
      default:
        return (
          <First nextStep={nextStep} handleChange={handleChange} form={form} />
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex flex-col">
      {/* Top Header Section */}
      <div className="w-full flex items-center">
        <BackButton />
        <TopAdminNav name="Jane Doe" title="Pupil Enrollment" />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Side: Quiet progress rail */}
        <aside className="hidden xl:flex w-[300px] bg-white border-r border-slate-200 flex-col p-6 space-y-6 overflow-y-auto">
          <div>
            <h2 className="text-xl font-black text-slate-900 tracking-tight">
              New Pupil Registration
            </h2>
            <p className="text-slate-500 text-sm mt-2 leading-relaxed">
              Complete each stage to add the pupil profile, health notes, and
              guardian details.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-slate-900">
            <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.18em] mb-5">
              Enrollment Progress
            </h3>
            <div className="space-y-4">
              <StepProgress
                number={1}
                label="Student Bio"
                active={step === 1}
                completed={step > 1}
              />
              <StepProgress
                number={2}
                label="Health & Medical"
                active={step === 2}
                completed={step > 2}
              />
              <StepProgress
                number={3}
                label="Guardian & Finalize"
                active={step === 3}
                completed={step > 3}
              />
            </div>
          </div>

          <div className="mt-auto rounded-2xl border border-blue-100 bg-blue-50/70 p-4 text-xs leading-relaxed text-slate-600">
            The main form has the full working area. Use this rail only to check
            where you are in the process.
          </div>
        </aside>

        {/* Main Content Pane */}
        <main className="flex-1 overflow-y-auto bg-[#F8FAFC] pb-20 custom-scrollbar">
          <div className="max-w-5xl mx-auto py-8 px-4 md:px-8">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

/* --- Progress Sub-Component --- */
const StepProgress = ({ number, label, active, completed }: any) => (
  <div className="flex items-center gap-4 group">
    <div
      className={`
      w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all duration-500
      ${completed ? "bg-emerald-500 text-white" : active ? "bg-[#012063] text-white shadow-sm" : "bg-white text-slate-400 border border-slate-200"}
    `}
    >
      {completed ? <UserCheck size={16} /> : number}
    </div>
    <div className="flex flex-col">
      <span
        className={`text-sm font-bold tracking-wide transition-colors ${active || completed ? "text-slate-900" : "text-slate-500"}`}
      >
        {label}
      </span>
      {active && (
        <span className="text-[10px] text-[#0096D1] font-bold uppercase">
          In Progress
        </span>
      )}
    </div>
  </div>
);
